import { img } from "../images";
import type { Blog } from "./types";

const DR_ARUN = "Dr. M. V. Arun Vijaiyan";
const ARUN_BIO =
  "Dr. M. V. Arun Vijaiyan is a Urologist and Andrologist at AV Clinic, Krishnagiri, with an M.S. and M.Ch. (Uro). He focuses on kidney stone care, urinary and prostate health, and male reproductive and sexual health, with a patient-centred approach to diagnosis and treatment.";
const ARUN_ROLE = "Urologist & Andrologist at AV Clinic, Krishnagiri";

export const urologyBlogs: Blog[] = [
  {
    slug: "kidney-stone-symptoms",
    title: "Common Symptoms of Kidney Stones",
    category: "Kidney Health",
    excerpt:
      "Kidney stones can cause severe, cramping pain. Learn the common symptoms and when a stone needs urgent medical evaluation.",
    image: img.svcKidneyStones,
    imageAlt: "Kidney stone symptoms and causes explained",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-08-01",
    modified: "2026-08-01",
    readTime: "5 min read",
    popular: true,
    tags: ["kidney stones", "kidney pain", "urology", "blood in urine"],
    primaryKeyword: "kidney stone symptoms",
    secondaryKeywords: ["kidney stone pain", "stone in kidney", "kidney stones in Krishnagiri"],
    relatedService: "kidney-stones",
    relatedServiceLabel: "Kidney Stone Treatment",
    relatedArticles: ["kidney-stone-causes", "kidney-stone-treatment", "hydronephrosis"],
    sections: [
      {
        heading: "What are kidney stones?",
        body: [
          "Kidney stones are hard, crystal-like deposits that form in the kidneys when minerals and salts in the urine become concentrated. Some stones are tiny and pass unnoticed, while others grow large enough to cause symptoms.",
        ],
      },
      {
        heading: "Common symptoms",
        body: ["A stone usually causes symptoms when it moves into the ureter and blocks the flow of urine. Common signs include:"],
        list: [
          "Severe, cramping pain in the back or side that comes in waves",
          "Pain that radiates to the lower abdomen or groin",
          "Blood in the urine (visible or only on a test)",
          "Burning or urgency while passing urine",
          "Nausea or vomiting during an episode of pain",
        ],
      },
      {
        heading: "When it becomes urgent",
        body: [
          "A kidney stone should be evaluated promptly if you have a fever, severe pain that does not settle, or difficulty passing urine. These can suggest an infection or blockage and need urgent medical attention.",
        ],
      },
      {
        heading: "How kidney stones are investigated",
        body: [
          "A urologist typically starts with a discussion of your symptoms and a urine test, followed by imaging such as an ultrasound or CT scan to confirm and size the stone.",
          "Understanding the size and position of the stone guides whether it is likely to pass on its own or needs specialist management.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can a kidney stone pass without treatment?",
        a: "Small stones often pass with hydration, pain control and supportive care. The chance of passing depends on the stone's size and position, which a urologist can assess.",
      },
      {
        q: "Does kidney stone pain come and go?",
        a: "Yes. The pain is typically colicky, meaning it comes in waves as the stone moves. It can be severe and sudden.",
      },
    ],
    seo: {
      title: "Kidney Stone Symptoms: When to See a Urologist | AV Clinic",
      description:
        "Learn the common symptoms of kidney stones in Krishnagiri, including back pain, blood in urine and nausea, and when to see a urologist at AV Clinic.",
    },
  },
  {
    slug: "kidney-stone-causes",
    title: "Kidney Stone Causes and Risk Factors",
    category: "Kidney Health",
    excerpt:
      "Why do kidney stones form? Understand the underlying causes and the risk factors that make some people more likely to develop stones.",
    image: img.svcKidneyStones,
    imageAlt: "Causes and risk factors for kidney stones",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-28",
    modified: "2026-07-28",
    readTime: "5 min read",
    tags: ["kidney stones", "dehydration", "diet", "kidney health"],
    primaryKeyword: "kidney stone causes",
    secondaryKeywords: ["kidney stone risk factors", "what causes kidney stones", "kidney stones prevention"],
    relatedService: "kidney-stones",
    relatedServiceLabel: "Kidney Stone Treatment",
    relatedArticles: ["kidney-stone-symptoms", "kidney-stone-prevention", "kidney-health"],
    sections: [
      {
        heading: "Why stones form",
        body: [
          "Kidney stones form when the urine becomes too concentrated, allowing minerals such as calcium, oxalate or uric acid to crystalise and clump together. The most common type of stone is calcium-based.",
        ],
      },
      {
        heading: "Key risk factors",
        body: ["Certain conditions make stone formation more likely:"],
        list: [
          "Not drinking enough water, especially in a hot climate",
          "A diet high in salt or processed foods",
          "A family history of kidney stones",
          "Metabolic or hormonal conditions affecting urine chemistry",
          "A past episode of kidney stones",
        ],
      },
      {
        heading: "Who is at higher risk",
        body: [
          "People who live in warm regions, work outdoors, or do not drink enough fluids are more likely to form stones. Recurrent stones often point to an underlying tendency worth investigating.",
        ],
      },
      {
        heading: "What you can do",
        body: [
          "Adequate hydration is the single most helpful step for most people. Your urologist can also look into the type of stone and give dietary advice tailored to your situation.",
        ],
      },
    ],
    faqs: [
      {
        q: "Does drinking less water cause stones?",
        a: "Low fluid intake is a leading contributor, as it concentrates the urine. Staying well hydrated reduces the concentration of stone-forming minerals.",
      },
      {
        q: "Can kidney stones be prevented?",
        a: "Often yes. Drinking enough water, moderating salt and following dietary advice specific to your stone type can significantly lower the risk of recurrence.",
      },
    ],
    seo: {
      title: "Kidney Stone Causes and Risk Factors | AV Clinic",
      description:
        "Understand what causes kidney stones and the risk factors that raise your chance of forming them. Kidney stone causes explained by AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "kidney-stone-treatment",
    title: "Kidney Stone Treatment Options",
    category: "Kidney Health",
    excerpt:
      "Not every kidney stone needs surgery. Learn the treatment options, from hydration and pain control to laser and minimally invasive procedures.",
    image: img.svcLaserKidneyStone,
    imageAlt: "Kidney stone treatment options including laser procedures",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-25",
    modified: "2026-07-25",
    readTime: "6 min read",
    featured: true,
    tags: ["kidney stones", "laser treatment", "endourology", "urology"],
    primaryKeyword: "kidney stone treatment",
    secondaryKeywords: ["laser kidney stone treatment", "kidney stone surgery", "endourology Krishnagiri"],
    relatedService: "laser-kidney-stone-treatment",
    relatedServiceLabel: "Laser Kidney Stone Treatment",
    relatedArticles: ["kidney-stone-symptoms", "kidney-stone-causes", "when-to-see-urologist"],
    sections: [
      {
        heading: "Do all stones need treatment?",
        body: [
          "No. Small stones that are not causing symptoms or blockage often pass on their own with hydration, pain relief and supportive care. Larger, painful or non-passing stones usually need specialist management.",
        ],
      },
      {
        heading: "Medical and supportive care",
        body: [
          "For stones that can pass, a urologist may advise increased fluids, pain control and, in some cases, medication to help relax the ureter and ease passage. This is watched closely with follow-up.",
        ],
      },
      {
        heading: "Minimally invasive procedures",
        body: ["When intervention is needed, modern urology takes a minimally invasive approach:"],
        list: [
          "Laser stone treatment to break stones into tiny fragments",
          "Endourological procedures that reach the stone through the urinary tract",
          "Techniques chosen based on the stone's size, location and your overall health",
        ],
      },
      {
        heading: "What to expect",
        body: [
          "The right option depends on your individual situation, not just the size of the stone. A urologist will explain the choices, their expected outcomes and any recovery considerations so you can decide with confidence.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is laser treatment for kidney stones common?",
        a: "Yes. Laser stone treatment is a well-established, minimally invasive option for suitable stones, performed through the urinary tract without a large cut.",
      },
      {
        q: "Will I need a long hospital stay?",
        a: "Many minimally invasive stone procedures allow a short stay and quicker recovery. Your urologist can advise based on your individual case.",
      },
    ],
    seo: {
      title: "Kidney Stone Treatment Options in Krishnagiri | AV Clinic",
      description:
        "Explore kidney stone treatment options in Krishnagiri, from hydration and pain control to laser and endourological procedures. Expert care at AV Clinic.",
    },
  },
  {
    slug: "kidney-stone-prevention",
    title: "How to Prevent Kidney Stones",
    category: "Kidney Health",
    excerpt:
      "Kidney stones often return. Learn the practical steps that reduce your risk of forming new stones.",
    image: img.svcUroConsult,
    imageAlt: "Preventing kidney stones with hydration and lifestyle changes",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-20",
    modified: "2026-07-20",
    readTime: "5 min read",
    tags: ["kidney stones", "hydration", "prevention", "kidney health"],
    primaryKeyword: "how to prevent kidney stones",
    secondaryKeywords: ["prevent kidney stones", "kidney stones prevention", "drink water kidney stones"],
    relatedService: "urological-consultation",
    relatedServiceLabel: "Urology Consultation",
    relatedArticles: ["kidney-stone-causes", "kidney-stone-symptoms", "drinking-water"],
    sections: [
      {
        heading: "Hydration is the foundation",
        body: [
          "Drinking enough water keeps the urine dilute, which is the single most important step in preventing stones. Aim for urine that is pale rather than dark or concentrated.",
        ],
      },
      {
        heading: "Dietary habits that help",
        body: ["Along with fluids, these habits support kidney health:"],
        list: [
          "Moderating salt and processed food intake",
          "Eating calcium-containing foods normally rather than avoiding them",
          "Watching intake of foods very high in oxalate, if advised",
          "Limiting excess sugary drinks",
        ],
      },
      {
        heading: "Know your stone",
        body: [
          "The best prevention plan depends on the type of stone you have formed. Understanding your stone's composition lets your urologist give specific dietary and lifestyle advice.",
        ],
      },
      {
        heading: "Ongoing follow-up",
        body: [
          "If you have had a stone, regular follow-up and simple urine checks help catch a recurrence early. Preventing stones is easier and more comfortable than treating them.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much water should I drink to prevent stones?",
        a: "This varies, but a target of about 2 to 3 litres a day is often suggested. Your urologist will advise an amount suited to you.",
      },
      {
        q: "Should I avoid calcium to prevent stones?",
        a: "Usually no. Normal dietary calcium can help reduce some types of stones. Talk to your urologist rather than cutting it out on your own.",
      },
    ],
    seo: {
      title: "How to Prevent Kidney Stones Naturally | AV Clinic",
      description:
        "Practical steps to prevent kidney stones in Krishnagiri — hydration, diet and follow-up. Kidney stone prevention advice from AV Clinic's urologists.",
    },
  },
  {
    slug: "blood-in-urine",
    title: "What Does Blood in Urine Mean?",
    category: "Urology",
    excerpt:
      "Blood in the urine — visible or only on a test — should always be evaluated. Here's what it can mean and what to do.",
    image: img.svcUrinaryProblems,
    imageAlt: "Blood in urine evaluation and causes",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-18",
    modified: "2026-07-18",
    readTime: "5 min read",
    popular: true,
    tags: ["blood in urine", "hematuria", "urology", "kidney health"],
    primaryKeyword: "blood in urine",
    secondaryKeywords: ["hematuria causes", "blood in urine meaning", "blood in urine Krishnagiri"],
    relatedService: "urinary-problems",
    relatedServiceLabel: "Urinary Health",
    relatedArticles: ["when-to-see-urologist", "kidney-stone-symptoms", "painful-urination"],
    sections: [
      {
        heading: "Blood in urine is always worth checking",
        body: [
          "Blood in the urine — whether the urine looks pink or red, or blood is found only under the microscope — is a symptom that should always be evaluated. It can be caused by several conditions, and the cause is not always obvious.",
        ],
      },
      {
        heading: "Possible causes",
        body: ["Common causes of blood in urine include:"],
        list: [
          "Urinary tract infections",
          "Kidney or ureteric stones",
          "Prostate-related changes in men",
          "Inflammation of the bladder or kidneys",
          "In rarer cases, more serious conditions of the urinary tract",
        ],
      },
      {
        heading: "What to do",
        body: [
          "A urological assessment is the recommended next step. This may include a urine test and imaging to identify the source. The goal is to find the cause and address it appropriately.",
        ],
      },
      {
        heading: "When it is urgent",
        body: [
          "If blood in the urine is accompanied by severe pain, fever, an inability to pass urine, or large visible clots, seek urgent medical care without delay.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is blood in urine always serious?",
        a: "Not necessarily, but it should always be evaluated because it can indicate a range of conditions that a urologist can assess and manage.",
      },
      {
        q: "Can a UTI cause blood in urine?",
        a: "Yes. A urinary tract infection is a common cause of visible blood in the urine and is usually treatable.",
      },
    ],
    seo: {
      title: "Blood in Urine: Causes & What to Do | AV Clinic",
      description:
        "Understand what blood in urine can mean and why it always needs evaluation. Urology advice and treatment at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "frequent-urination",
    title: "Causes of Frequent Urination",
    category: "Bladder Health",
    excerpt:
      "Needing to urinate all day or all night is exhausting. Learn the common causes and when it deserves attention.",
    image: img.svcMaleUrinary,
    imageAlt: "Frequent urination causes and evaluation",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-15",
    modified: "2026-07-15",
    readTime: "5 min read",
    tags: ["frequent urination", "urinary frequency", "overactive bladder", "urology"],
    primaryKeyword: "frequent urination causes",
    secondaryKeywords: ["urinating often", "frequent urination Krishnagiri", "why do I urinate so much"],
    relatedService: "urinary-problems",
    relatedServiceLabel: "Urinary Health",
    relatedArticles: ["overactive-bladder", "night-time-urination", "when-to-see-urologist"],
    sections: [
      {
        heading: "What counts as frequent urination",
        body: [
          "Frequent urination means needing to urinate noticeably more often than usual, or waking repeatedly through the night to pass urine. It can disrupt work, sleep and daily life.",
        ],
      },
      {
        heading: "Possible causes",
        body: ["The cause may be simple or may need evaluation:"],
        list: [
          "High fluid intake or excess caffeine, tea and alcohol",
          "Urinary tract infections",
          "An overactive bladder",
          "Prostate changes in men",
          "Certain medications or medical conditions",
        ],
      },
      {
        heading: "When to see a urologist",
        body: [
          "If frequency is persistent, worsening, or accompanied by pain, blood, urgency or leakage, a urological evaluation helps identify the cause and guide appropriate care.",
        ],
      },
      {
        heading: "Tracking helps",
        body: [
          "Keeping a simple record of how often you pass urine and how much you drink can be very helpful for a urologist to understand your pattern.",
        ],
      },
    ],
    faqs: [
      {
        q: "Could frequent urination be from what I drink?",
        a: "Yes. Coffee, tea, alcohol and high fluid intake can increase urination. But persistent or worsening frequency should still be evaluated.",
      },
      {
        q: "Is frequent urination a sign of diabetes?",
        a: "High blood sugar can cause increased urination. If you have other signs such as excessive thirst, a check-up is worthwhile.",
      },
    ],
    seo: {
      title: "Frequent Urination: Causes & When to Act | AV Clinic",
      description:
        "Learn the common causes of frequent urination and when to see a urologist in Krishnagiri. Bladder and urinary health guidance at AV Clinic.",
    },
  },
  {
    slug: "uti-symptoms",
    title: "Urinary Tract Infection: Symptoms to Know",
    category: "Urology",
    excerpt:
      "A urinary tract infection can be uncomfortable and can worsen if left untreated. Learn the symptoms you should not ignore.",
    image: img.svcUti,
    imageAlt: "Urinary tract infection symptoms and treatment",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-12",
    modified: "2026-07-12",
    readTime: "5 min read",
    popular: true,
    tags: ["UTI", "urinary infection", "burning urination", "urology"],
    primaryKeyword: "urinary tract infection symptoms",
    secondaryKeywords: ["UTI symptoms", "burning when urinating", "urinary infection Krishnagiri"],
    relatedService: "uti-management",
    relatedServiceLabel: "UTI Management",
    relatedArticles: ["uti-causes-prevention", "painful-urination", "symptoms-not-to-ignore"],
    sections: [
      {
        heading: "What is a UTI?",
        body: [
          "A urinary tract infection (UTI) is an infection in any part of the urinary system, most commonly the bladder or urethra. It happens when bacteria enter the urinary tract and multiply.",
        ],
      },
      {
        heading: "Common symptoms",
        body: ["Symptoms of a urinary infection often include:"],
        list: [
          "A strong, persistent urge to urinate",
          "A burning sensation when passing urine",
          "Passing small amounts frequently",
          "Cloudy, strong-smelling or blood-tinged urine",
          "Pelvic discomfort in women",
        ],
      },
      {
        heading: "When to seek care",
        body: [
          "If you suspect a UTI, prompt treatment is important. Untreated infections can sometimes spread to the kidneys, so a urine test and appropriate management are recommended.",
        ],
      },
      {
        heading: "Recurrent infections",
        body: [
          "If you get frequent UTIs, a urologist can look into contributing factors and help reduce the chance of recurrence.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can a UTI go away on its own?",
        a: "Some mild infections may settle, but a confirmed UTI usually benefits from treatment to clear the infection and prevent complications.",
      },
      {
        q: "Do I always need a test for a UTI?",
        a: "A urine test helps confirm the infection and identify the right treatment. It is a simple and worthwhile step.",
      },
    ],
    seo: {
      title: "Urinary Tract Infection Symptoms & Care | AV Clinic",
      description:
        "Recognise the symptoms of a urinary tract infection and when to seek care. UTI diagnosis and treatment at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "uti-causes-prevention",
    title: "UTI Causes and How to Prevent Them",
    category: "Urology",
    excerpt:
      "Understand why urinary infections happen and the simple habits that help prevent them.",
    image: img.svcUti,
    imageAlt: "Urinary tract infection causes and prevention",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-08",
    modified: "2026-07-08",
    readTime: "5 min read",
    tags: ["UTI", "prevention", "urinary health", "hydration"],
    primaryKeyword: "UTI prevention",
    secondaryKeywords: ["prevent urinary infection", "UTI causes", "urinary tract infection prevention"],
    relatedService: "uti-management",
    relatedServiceLabel: "UTI Management",
    relatedArticles: ["uti-symptoms", "bladder-health-tips", "drinking-water"],
    sections: [
      {
        heading: "Why UTIs happen",
        body: [
          "Most UTIs are caused by bacteria from the bowel entering the urinary tract and multiplying. Women are more prone to UTIs due to a shorter urethra, though men can also get them.",
        ],
      },
      {
        heading: "Habits that reduce risk",
        body: ["Simple daily practices can help:"],
        list: [
          "Drinking enough water to flush the urinary tract",
          "Urinating when you feel the urge rather than holding for long periods",
          "Emptying the bladder fully",
          "Good hygiene practices",
          "Avoiding harsh or scented feminine products",
        ],
      },
      {
        heading: "When prevention is not enough",
        body: [
          "Some people get recurrent infections despite good habits. If this applies to you, a urological review can identify contributing factors and tailor a prevention plan.",
        ],
      },
      {
        heading: "Seek care early",
        body: [
          "Treating a suspected infection early is more comfortable and lowers the risk of the infection reaching the kidneys.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can stress cause UTIs?",
        a: "Stress alone does not cause UTIs, but it can affect the body's resilience and habits. The main causes are bacterial entry into the urinary tract.",
      },
      {
        q: "Does drinking more water help prevent UTIs?",
        a: "Yes. Staying hydrated dilutes the urine and helps flush out bacteria, which can reduce the risk of infection.",
      },
    ],
    seo: {
      title: "UTI Causes and Prevention Tips | AV Clinic",
      description:
        "Learn what causes urinary tract infections and simple habits to prevent them. UTI prevention advice from AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "overactive-bladder",
    title: "Overactive Bladder: Symptoms and What Helps",
    category: "Bladder Health",
    excerpt:
      "A sudden, hard-to-control urge to urinate can disrupt your day. Understand overactive bladder and its management.",
    image: img.svcBladderDisorders,
    imageAlt: "Overactive bladder symptoms and management",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-05",
    modified: "2026-07-05",
    readTime: "5 min read",
    tags: ["overactive bladder", "urinary urgency", "bladder health", "urology"],
    primaryKeyword: "overactive bladder symptoms",
    secondaryKeywords: ["overactive bladder", "sudden urge to urinate", "bladder urgency"],
    relatedService: "bladder-disorders",
    relatedServiceLabel: "Bladder Health",
    relatedArticles: ["frequent-urination", "night-time-urination", "bladder-health-tips"],
    sections: [
      {
        heading: "What is overactive bladder?",
        body: [
          "Overactive bladder (OAB) is a condition in which the bladder muscle contracts more often than it should, creating a sudden, urgent and sometimes hard-to-control need to urinate.",
        ],
      },
      {
        heading: "Common symptoms",
        body: ["Symptoms of overactive bladder include:"],
        list: [
          "A sudden, intense urge to urinate that is hard to delay",
          "Urinating very frequently, often more than eight times a day",
          "Waking one or more times at night to pass urine",
          "Leaking urine when the urge strikes",
        ],
      },
      {
        heading: "Why it happens",
        body: [
          "OAB can result from changes in bladder function, urinary infections, prostate changes in men, certain medications and neurological conditions. Because causes vary, proper evaluation is important.",
        ],
      },
      {
        heading: "Management",
        body: [
          "Management often begins with bladder training and lifestyle changes, such as cutting down on caffeine and staying well hydrated. In selected cases, medication or other specialist options may help. A urologist will tailor the approach to you.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is overactive bladder a normal part of ageing?",
        a: "No. While urgency can be more common with age, persistent symptoms deserve evaluation as they can usually be improved.",
      },
      {
        q: "Can overactive bladder be cured?",
        a: "For many people, symptoms can be significantly controlled with the right approach, though it may require ongoing management.",
      },
    ],
    seo: {
      title: "Overactive Bladder: Symptoms & Management | AV Clinic",
      description:
        "Learn about overactive bladder symptoms and how a urologist in Krishnagiri can help manage urinary urgency. Bladder care guidance at AV Clinic.",
    },
  },
  {
    slug: "bladder-health-tips",
    title: "Bladder Health Tips for Everyday Life",
    category: "Bladder Health",
    excerpt:
      "Simple habits can keep your bladder healthy and comfortable. Here are practical tips to protect urinary health.",
    image: img.svcBladderDisorders,
    imageAlt: "Everyday tips for bladder health",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-07-01",
    modified: "2026-07-01",
    readTime: "4 min read",
    tags: ["bladder health", "urinary health", "hydration", "prevention"],
    primaryKeyword: "bladder health tips",
    secondaryKeywords: ["keep bladder healthy", "urinary health tips", "bladder care"],
    relatedService: "urinary-problems",
    relatedServiceLabel: "Urinary Health",
    relatedArticles: ["overactive-bladder", "frequent-urination", "uti-causes-prevention"],
    sections: [
      {
        heading: "Stay hydrated, but sensibly",
        body: [
          "Drinking enough water keeps urine dilute and helps the bladder flush out irritants. Large amounts of fluid late in the evening can increase night-time urination, so it helps to drink more earlier in the day.",
        ],
      },
      {
        heading: "Watch bladder irritants",
        body: ["Some foods and drinks can irritate the bladder:"],
        list: [
          "Excess caffeine from tea, coffee and cola",
          "Alcohol",
          "Very spicy or acidic foods, if they bother you",
          "Artificial sweeteners, in some people",
        ],
      },
      {
        heading: "Don't hold urine for long",
        body: [
          "Holding urine for long periods can overstretch the bladder and increase the chance of infection. Urinate when you feel the urge, and take time to empty the bladder fully.",
        ],
      },
      {
        heading: "Maintain a healthy weight and stay active",
        body: [
          "A healthy weight and regular activity support pelvic and urinary health. If you notice persistent urgency, frequency or leakage, seek a urological review.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much water should I drink for bladder health?",
        a: "Enough to keep urine pale and dilute. This varies, but a general guide is around 1.5 to 2 litres a day. Your urologist can advise for your situation.",
      },
      {
        q: "Does caffeine affect the bladder?",
        a: "Yes. Caffeine can increase urinary urgency and frequency in some people. Cutting down may help.",
      },
    ],
    seo: {
      title: "Bladder Health Tips for Everyday Life | AV Clinic",
      description:
        "Practical bladder health tips — hydration, reducing irritants and healthy habits. Bladder health guidance from AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "painful-urination",
    title: "Causes of Painful Urination",
    category: "Urology",
    excerpt:
      "Burning or discomfort while passing urine is common and usually treatable. Learn the possible causes and what to do.",
    image: img.svcUrinaryProblems,
    imageAlt: "Painful urination causes and evaluation",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-06-28",
    modified: "2026-06-28",
    readTime: "4 min read",
    tags: ["painful urination", "dysuria", "UTI", "urology"],
    primaryKeyword: "painful urination",
    secondaryKeywords: ["burning urination", "dysuria treatment", "pain when passing urine"],
    relatedService: "urinary-problems",
    relatedServiceLabel: "Urinary Health",
    relatedArticles: ["uti-symptoms", "blood-in-urine", "when-to-see-urologist"],
    sections: [
      {
        heading: "What painful urination can indicate",
        body: [
          "Painful urination, also called dysuria, is a burning or stinging feeling when passing urine. It is usually a sign of irritation or inflammation in the urinary tract.",
        ],
      },
      {
        heading: "Common causes",
        body: ["The most common causes include:"],
        list: [
          "Urinary tract infection",
          "Irritation from hygiene products or soap",
          "Kidney or bladder stones",
          "Inflammation of the prostate in men",
          "Sexually transmitted infections",
        ],
      },
      {
        heading: "When to seek care",
        body: [
          "If burning is persistent, or comes with blood, fever, or discharge, a proper evaluation is recommended. A simple urine test often provides the answer.",
        ],
      },
      {
        heading: "What you can do",
        body: [
          "Drinking plenty of water and avoiding irritants can ease symptoms. Persistent or severe pain deserves medical advice rather than just self-care.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is painful urination always an infection?",
        a: "No. It can be caused by several conditions. A urine test helps identify the cause.",
      },
      {
        q: "Can painful urination be treated at home?",
        a: "Mild symptoms may ease with hydration, but persistent pain should be evaluated to confirm the cause and get the right treatment.",
      },
    ],
    seo: {
      title: "Painful Urination: Causes & Treatment | AV Clinic",
      description:
        "Learn the common causes of painful urination and when to see a urologist in Krishnagiri. Diagnosis and treatment at AV Clinic.",
    },
  },
  {
    slug: "urinary-incontinence",
    title: "Urinary Incontinence in Adults: What to Know",
    category: "Bladder Health",
    excerpt:
      "Involuntary urine leakage is common and can be managed. Understand the types of incontinence and the help available.",
    image: img.svcBladderDisorders,
    imageAlt: "Urinary incontinence types and management",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-06-24",
    modified: "2026-06-24",
    readTime: "5 min read",
    tags: ["urinary incontinence", "urine leakage", "bladder control", "urology"],
    primaryKeyword: "urinary incontinence",
    secondaryKeywords: ["urine leakage", "bladder control", "incontinence treatment"],
    relatedService: "urinary-problems",
    relatedServiceLabel: "Urinary Health",
    relatedArticles: ["overactive-bladder", "frequent-urination", "bladder-health-tips"],
    sections: [
      {
        heading: "What is urinary incontinence?",
        body: [
          "Urinary incontinence is the involuntary loss of urine. It is a common and often distressing condition that many people are reluctant to discuss, but it can usually be improved with the right approach.",
        ],
      },
      {
        heading: "Common types",
        body: ["The main types include:"],
        list: [
          "Stress incontinence — leakage on coughing, laughing or lifting",
          "Urge incontinence — leakage with a sudden, strong urge to urinate",
          "Mixed incontinence — a combination of the two",
          "Overflow incontinence — leakage when the bladder does not empty fully",
        ],
      },
      {
        heading: "Causes and risk factors",
        body: [
          "Causes include weakened pelvic floor muscles, prostate changes in men, childbirth, ageing, certain medical conditions and some medications.",
        ],
      },
      {
        heading: "Getting help",
        body: [
          "The first step is a urological assessment to identify the type of incontinence and its causes. Management can include pelvic floor exercises, lifestyle changes, bladder training and, in selected cases, medication or specialist options.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is urine leakage a normal part of ageing?",
        a: "No. While more common with age, leakage is not something you simply have to accept and can often be improved.",
      },
      {
        q: "Can pelvic floor exercises help incontinence?",
        a: "For many people, yes. Pelvic floor strengthening exercises can improve stress and some types of urge incontinence when done correctly.",
      },
    ],
    seo: {
      title: "Urinary Incontinence: Types & Treatment | AV Clinic",
      description:
        "Understand the types and causes of urinary incontinence and the treatment options available. Incontinence care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "when-to-see-urologist",
    title: "When Should You See a Urologist?",
    category: "Urology",
    excerpt:
      "Some urinary symptoms should never be ignored. Learn the warning signs that warrant a urology consultation.",
    image: img.svcUroConsult,
    imageAlt: "When to see a urologist for urinary symptoms",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-06-20",
    modified: "2026-06-20",
    readTime: "6 min read",
    featured: true,
    tags: ["urologist", "urinary symptoms", "kidney health", "prostate health"],
    primaryKeyword: "when to see a urologist",
    secondaryKeywords: ["urologist Krishnagiri", "urinary symptoms", "see a urologist"],
    relatedService: "urological-consultation",
    relatedServiceLabel: "Urology Consultation",
    relatedArticles: ["blood-in-urine", "kidney-stone-symptoms", "prostate-problem-signs"],
    sections: [
      {
        heading: "The urinary system deserves attention",
        body: [
          "The urinary system includes the kidneys, ureters, bladder and urethra. Problems here can range from minor irritations to conditions that benefit from early specialist care.",
          "A urologist in Krishnagiri can evaluate symptoms, identify their cause and guide appropriate management.",
        ],
      },
      {
        heading: "Signs not to ignore",
        body: ["Consider a urology consultation if you experience any of these:"],
        list: [
          "Blood in the urine, visible or on a test",
          "Severe or recurrent flank or back pain",
          "Burning or painful urination that persists",
          "A persistent need to urinate frequently or urgently",
          "Difficulty starting to urinate, or a weak stream",
          "Recurrent urinary infections",
        ],
      },
      {
        heading: "Kidney stones and urinary health",
        body: [
          "Kidney stones are common and can be very painful. Evaluation helps determine whether a stone can be managed conservatively or needs a procedure such as laser stone treatment.",
        ],
      },
      {
        heading: "Don't wait",
        body: [
          "Urinary symptoms rarely improve by ignoring them. An early consultation often leads to simpler, more comfortable care.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need a referral to see a urologist?",
        a: "In most cases you can book a urology consultation directly without a referral.",
      },
      {
        q: "What should I bring to my first urology visit?",
        a: "Bring any recent reports, a list of your medications and a clear description of your symptoms. This helps the urologist assess you well.",
      },
    ],
    seo: {
      title: "When to See a Urologist: Warning Signs | AV Clinic",
      description:
        "Key warning signs that warrant a urology consultation in Krishnagiri — blood in urine, kidney stones, painful urination and more. Expert care at AV Clinic.",
    },
  },
  {
    slug: "hydronephrosis",
    title: "Hydronephrosis: Causes and Treatment",
    category: "Kidney Health",
    excerpt:
      "Hydronephrosis is a swelling of the kidney caused by a blockage of urine flow. Learn its causes and how it is managed.",
    image: img.svcKidneyStones,
    imageAlt: "Hydronephrosis causes and treatment",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-06-16",
    modified: "2026-06-16",
    readTime: "5 min read",
    tags: ["hydronephrosis", "kidney swelling", "kidney stones", "urology"],
    primaryKeyword: "hydronephrosis",
    secondaryKeywords: ["hydronephrosis causes", "kidney swelling", "hydronephrosis treatment"],
    relatedService: "ureteric-stone-treatment",
    relatedServiceLabel: "Ureteric Stone Treatment",
    relatedArticles: ["kidney-stone-symptoms", "kidney-stone-treatment", "kidney-health"],
    sections: [
      {
        heading: "What is hydronephrosis?",
        body: [
          "Hydronephrosis is the swelling of one or both kidneys that occurs when urine is unable to drain properly and backs up into the kidney. It is not a disease in itself, but a sign of an underlying blockage.",
        ],
      },
      {
        heading: "Common causes",
        body: ["Hydronephrosis is often caused by an obstruction to urine flow:"],
        list: [
          "A kidney stone blocking the ureter",
          "Narrowing of the ureter",
          "Prostate enlargement in men",
          "A mass pressing on the urinary tract",
        ],
      },
      {
        heading: "Symptoms",
        body: [
          "Some cases are found incidentally on a scan and cause no symptoms. When present, it may cause pain in the flank or back, blood in the urine, or recurrent infections.",
        ],
      },
      {
        heading: "How it is managed",
        body: [
          "Management aims to relieve the blockage and protect kidney function. Depending on the cause, this may involve treating the underlying problem, using techniques to drain the kidney, or a minimally invasive procedure. A urologist will advise the right approach.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is hydronephrosis dangerous?",
        a: "If left untreated, prolonged blockage can affect kidney function. Early evaluation and relieving the obstruction protects the kidney.",
      },
      {
        q: "Can hydronephrosis resolve on its own?",
        a: "If the cause is a small stone that passes, it may. Otherwise, the underlying blockage needs treatment.",
      },
    ],
    seo: {
      title: "Hydronephrosis: Causes & Treatment | AV Clinic",
      description:
        "Learn about hydronephrosis, a swelling of the kidney from blocked urine flow, its causes and treatment. Urology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "kidney-health",
    title: "Understanding Kidney Health",
    category: "Kidney Health",
    excerpt:
      "Your kidneys filter blood and keep your body balanced. Learn how to protect them and the signs that warrant attention.",
    image: img.svcKidneyStones,
    imageAlt: "Understanding kidney health and how to protect it",
    author: DR_ARUN,
    authorBio: ARUN_BIO,
    authorRole: ARUN_ROLE,
    date: "2026-06-12",
    modified: "2026-06-12",
    readTime: "6 min read",
    featured: true,
    tags: ["kidney health", "kidney stones", "hydration", "urology"],
    primaryKeyword: "kidney health",
    secondaryKeywords: ["protect kidneys", "kidney health tips", "kidney function Krishnagiri"],
    relatedService: "urological-consultation",
    relatedServiceLabel: "Urology Consultation",
    relatedArticles: ["kidney-stone-causes", "blood-in-urine", "hydronephrosis"],
    sections: [
      {
        heading: "What your kidneys do",
        body: [
          "Your two kidneys filter waste and excess fluid from the blood to make urine. They also help regulate blood pressure, balance electrolytes and support healthy bones. Good kidney function is central to overall health.",
        ],
      },
      {
        heading: "Habits that protect your kidneys",
        body: ["You can support kidney health with simple steps:"],
        list: [
          "Drinking enough water, especially in a hot climate",
          "Managing blood pressure and blood sugar if relevant",
          "Eating a balanced diet with moderate salt",
          "Avoiding excess painkillers and unregulated medications",
        ],
      },
      {
        heading: "Signs that warrant attention",
        body: [
          "Swelling around the ankles, blood in the urine, persistent flank pain, changes in urination, or a decline in overall energy can all relate to kidney health. These deserve evaluation.",
        ],
      },
      {
        heading: "When to seek care",
        body: [
          "Because kidney problems can develop quietly, a kidney health evaluation — including simple urine and blood tests — is a sensible step if you have risk factors or symptoms.",
        ],
      },
    ],
    faqs: [
      {
        q: "How can I keep my kidneys healthy?",
        a: "Stay hydrated, eat a balanced diet with moderate salt, keep blood pressure and sugar in range, and avoid unnecessary medications.",
      },
      {
        q: "What tests check kidney health?",
        a: "A simple urine test and blood tests that look at kidney function are commonly used. Your doctor can advise what is appropriate for you.",
      },
    ],
    seo: {
      title: "Understanding Kidney Health & How to Protect It | AV Clinic",
      description:
        "Learn how your kidneys work and the habits that protect them. Kidney health guidance and urology care at AV Clinic, Krishnagiri.",
    },
  },
];